import { useState } from 'react';
import { learningSubjects, Subject, Problem } from '../../../mocks/learningProblems';

interface LearningModeProps {
  onSolveProblem: (query: string) => void;
  onClose: () => void;
}

export default function LearningMode({ onSolveProblem, onClose }: LearningModeProps) {
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [completedProblems, setCompletedProblems] = useState<Set<string>>(new Set());
  const [expandedHints, setExpandedHints] = useState<Set<string>>(new Set());

  const handleSubjectClick = (subject: Subject) => {
    setSelectedSubject(subject);
  };

  const handleBackToSubjects = () => {
    setSelectedSubject(null);
  };

  const handleSolveProblem = (problem: Problem) => {
    onSolveProblem(problem.query);
    setCompletedProblems(prev => new Set([...prev, problem.id]));
  };

  const toggleHint = (problemId: string) => {
    setExpandedHints(prev => {
      const newSet = new Set(prev);
      if (newSet.has(problemId)) {
        newSet.delete(problemId);
      } else {
        newSet.add(problemId);
      }
      return newSet;
    });
  };

  const getNextProblem = (currentProblem: Problem): Problem | null => {
    if (!selectedSubject) return null;
    const currentIndex = selectedSubject.problems.findIndex(p => p.id === currentProblem.id);
    if (currentIndex < selectedSubject.problems.length - 1) {
      return selectedSubject.problems[currentIndex + 1];
    }
    return null;
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner':
        return 'bg-emerald-100 text-emerald-700 border-emerald-300';
      case 'Intermediate':
        return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'Advanced':
        return 'bg-rose-100 text-rose-700 border-rose-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getProgressPercentage = () => {
    if (!selectedSubject) return 0;
    const completed = selectedSubject.problems.filter(p => completedProblems.has(p.id)).length;
    return (completed / selectedSubject.problems.length) * 100;
  };

  if (!selectedSubject) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6 flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">Learning Mode</h2>
              <p className="text-indigo-100">Choose a subject to start your guided learning journey</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors text-white"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Subject Grid */}
          <div className="flex-1 overflow-y-auto p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {learningSubjects.map((subject) => {
                const completed = subject.problems.filter(p => completedProblems.has(p.id)).length;
                const progress = (completed / subject.problemCount) * 100;

                return (
                  <button
                    key={subject.id}
                    onClick={() => handleSubjectClick(subject)}
                    className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-indigo-400 hover:shadow-lg transition-all duration-300 text-left"
                  >
                    <div className={`w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br ${subject.color} mb-4 group-hover:scale-110 transition-transform`}>
                      <i className={`${subject.icon} text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{subject.name}</h3>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm text-gray-600">{subject.problemCount} Problems</span>
                      {completed > 0 && (
                        <span className="text-sm font-semibold text-emerald-600">
                          • {completed} Completed
                        </span>
                      )}
                    </div>
                    {progress > 0 && (
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-full bg-gradient-to-r ${subject.color} transition-all duration-500`}
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Problem Set View
  const progress = getProgressPercentage();
  const completedCount = selectedSubject.problems.filter(p => completedProblems.has(p.id)).length;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`bg-gradient-to-r ${selectedSubject.color} px-8 py-6`}>
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handleBackToSubjects}
              className="flex items-center gap-2 text-white hover:bg-white/20 px-4 py-2 rounded-lg transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Back to Subjects</span>
            </button>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors text-white"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-white/20`}>
              <i className={`${selectedSubject.icon} text-2xl text-white`}></i>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white">{selectedSubject.name}</h2>
              <p className="text-white/90">
                {completedCount} / {selectedSubject.problemCount} Completed
              </p>
            </div>
          </div>
          {/* Progress Bar */}
          <div className="w-full bg-white/20 rounded-full h-3 overflow-hidden">
            <div
              className="h-full bg-white transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Problems List */}
        <div className="flex-1 overflow-y-auto p-8">
          <div className="space-y-4">
            {selectedSubject.problems.map((problem, index) => {
              const isCompleted = completedProblems.has(problem.id);
              const isHintExpanded = expandedHints.has(problem.id);
              const nextProblem = getNextProblem(problem);

              return (
                <div
                  key={problem.id}
                  className={`border-2 rounded-xl p-6 transition-all ${
                    isCompleted
                      ? 'border-emerald-300 bg-emerald-50'
                      : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`w-10 h-10 flex items-center justify-center rounded-full font-bold text-lg flex-shrink-0 ${
                        isCompleted ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-700'
                      }`}>
                        {isCompleted ? <i className="ri-check-line"></i> : index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getDifficultyColor(problem.difficulty)}`}>
                            {problem.difficulty}
                          </span>
                          {isCompleted && (
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-300">
                              ✓ Completed
                            </span>
                          )}
                        </div>
                        <p className="text-lg text-gray-800 font-medium mb-3">{problem.statement}</p>
                        
                        {/* Hint Toggle */}
                        <button
                          onClick={() => toggleHint(problem.id)}
                          className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium mb-2"
                        >
                          <i className={`ri-lightbulb-${isHintExpanded ? 'fill' : 'line'}`}></i>
                          <span>{isHintExpanded ? 'Hide Hint' : 'Show Hint'}</span>
                        </button>

                        {isHintExpanded && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-3">
                            <p className="text-sm text-amber-900">
                              <i className="ri-lightbulb-fill text-amber-600 mr-2"></i>
                              {problem.hint}
                            </p>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleSolveProblem(problem)}
                            className={`px-6 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                              isCompleted
                                ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg'
                            }`}
                          >
                            <i className="ri-play-circle-line mr-2"></i>
                            {isCompleted ? 'Solve Again' : 'Solve This'}
                          </button>

                          {isCompleted && nextProblem && (
                            <button
                              onClick={() => handleSolveProblem(nextProblem)}
                              className="px-6 py-2 rounded-lg font-semibold bg-emerald-500 text-white hover:bg-emerald-600 transition-all whitespace-nowrap"
                            >
                              Next Problem
                              <i className="ri-arrow-right-line ml-2"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Completion Message */}
          {completedCount === selectedSubject.problemCount && (
            <div className="mt-8 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-8 text-center text-white">
              <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                <i className="ri-trophy-line text-4xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-2">Congratulations!</h3>
              <p className="text-emerald-50 mb-4">
                You've completed all {selectedSubject.problemCount} problems in {selectedSubject.name}!
              </p>
              <button
                onClick={handleBackToSubjects}
                className="px-6 py-3 bg-white text-emerald-600 rounded-lg font-semibold hover:bg-emerald-50 transition-colors whitespace-nowrap"
              >
                Choose Another Subject
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}